import { isSensitiveParam } from "./telemetry-sanitizer.js";

//#region src/web/envelope-rules.ts
const CLOUD_ROLE_TAG = "ai.cloud.role";
const LOGIN_CALLBACK_PATH = "login/callback";
const EXCEPTION_BASE_TYPE = "ExceptionData";
const STANDALONE_DISPLAY_QUERY = "(display-mode: standalone)";
const PATHWAY_PARAM = "pathway";
/** Matches the JS bridges injected by common in-app-browser WebViews (opened
* from a texted/emailed deep link) so exception telemetry can be correlated
* against that population instead of the standalone Safari/PWA one. */
const IN_APP_BROWSER_UA_PATTERN = /FBAN|FBAV|Instagram|GSA|Line/;
/** Dropped because a stripped cross-origin error has nothing left to debug. */
const SANITIZED_CROSS_ORIGIN_MESSAGES = [
	"Script error.",
	"ErrorEvent: Script error.",
	"Script error",
	"ErrorEvent: Script error"
];
/**
* Whether an envelope must never be sent: page telemetry for the login
* callback, whose URL carries auth material, and stackless cross-origin
* "Script error." exceptions, which have nothing to debug.
*/
function shouldDrop(envelope) {
	const uri = envelope.baseData?.uri;
	if (typeof uri === "string" && uri.includes(LOGIN_CALLBACK_PATH)) return true;
	return isSanitizedScriptError(envelope);
}
/**
* Computes the tags and data to add to an envelope. Always yields the cloud
* role tag, plus `build_version` when a build number is known. For exception
* envelopes it also yields `hadErrorObject`, `userAgent`, `isStandaloneDisplay`,
* `isInAppBrowser`, and `pathway` (only when present and not sensitive).
*
* Pure: never mutates the envelope. Pass the result to `applyEnrichment`.
*/
function enrichmentFor(envelope, context, environment) {
	const buildData = context.buildNumber ? { build_version: context.buildNumber } : {};
	const exceptionData = envelope.baseType === EXCEPTION_BASE_TYPE ? exceptionFields(envelope, environment) : {};
	return {
		tags: { [CLOUD_ROLE_TAG]: context.cloudRole },
		data: {
			...buildData,
			...exceptionData
		}
	};
}
/**
* Writes an enrichment onto an envelope, creating `tags` and `data` when
* missing. This is the single mutation in the module. Values are assigned onto
* the existing `tags` object because the SDK models it as an array carrying
* named properties; replacing it would drop what other plugins already set.
*/
function applyEnrichment(envelope, enrichment) {
	envelope.tags ??= [];
	envelope.data ??= {};
	Object.assign(envelope.tags, enrichment.tags);
	Object.assign(envelope.data, enrichment.data);
}
/**
* Reads the browser state the rules depend on; the only effectful function
* here. Each value is read on access, so `window.matchMedia`, which throws
* where it is missing, is only touched when `isStandaloneDisplay` is read.
*/
function readBrowserEnvironment() {
	return {
		get userAgent() {
			return navigator.userAgent;
		},
		get isStandaloneDisplay() {
			return window.matchMedia(STANDALONE_DISPLAY_QUERY).matches;
		},
		get search() {
			return window.location.search;
		}
	};
}
function exceptionFields(envelope, environment) {
	/** AppInsights' own signal for whether it extracted a real stack
	* (exceptions[0].hasFullStack). False here means a stackless error that is
	* not sanitized cross-origin noise, e.g. a thrown non-Error or an onerror
	* call without an error object. Reading it off the envelope avoids any
	* dependency on listener registration order or timing. */
	const hasFullStack = envelope.baseData?.exceptions?.[0]?.hasFullStack;
	return {
		hadErrorObject: Boolean(hasFullStack),
		userAgent: environment.userAgent,
		isStandaloneDisplay: environment.isStandaloneDisplay,
		isInAppBrowser: IN_APP_BROWSER_UA_PATTERN.test(environment.userAgent),
		...pathwayField(environment.search)
	};
}
function pathwayField(search) {
	const pathway = new URLSearchParams(search).get(PATHWAY_PARAM);
	return pathway && !isSensitiveParam(PATHWAY_PARAM, pathway) ? { [PATHWAY_PARAM]: pathway } : {};
}
/** The browser stripped everything but the message, so there is nothing to debug. */
function isSanitizedScriptError(envelope) {
	const exception = envelope.baseData?.exceptions?.[0];
	return envelope.baseType === EXCEPTION_BASE_TYPE && !exception?.hasFullStack && SANITIZED_CROSS_ORIGIN_MESSAGES.includes(exception?.message);
}

//#endregion
export { applyEnrichment, enrichmentFor, readBrowserEnvironment, shouldDrop };